import * as yup from 'yup';

import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { SatelliteType, States } from '../common/Data';

import React, { useCallback, useMemo } from 'react';
import { Satellite } from '../common/Types';
import { programService } from '../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

interface SatelliteFormProps {
    onSave: () => void;
    onCancel: () => void;
    programId: number;
    satellite?: Satellite;
}

// Form data interface with proper typing
interface SatelliteFormData {
    satelliteId: number;
    programId: number;
    title?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    satalliteTypeId: number;
    address?: string;
    dateCreated: string;
    isActive: boolean;
}

const SatelliteForm = (props: SatelliteFormProps) => {
    // Memoize validation schema
    const satelliteSchema = useMemo(
        () =>
            yup.object<SatelliteFormData>({
                satelliteId: yup
                    .number()
                    .required()
                    .default(props.satellite?.satelliteId || 0),
                programId: yup.number().required().default(props.programId),
                title: yup.string().optional().nullable(),
                city: yup.string().optional().nullable(),
                state: yup.string().optional().nullable(),
                postalCode: yup.string().optional().nullable(),
                satalliteTypeId: yup.number().required().default(SatelliteType['Satellite Campus']),
                address: yup.string().optional().nullable(),
                dateCreated: yup.string().required(),
                isActive: yup.boolean().required().default(true),
            }),
        [props.satellite, props.programId],
    );

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { isSubmitting, errors, isDirty },
    } = useForm<SatelliteFormData>({
        resolver: yupResolver(satelliteSchema) as any,
        mode: 'onChange',
        defaultValues: {
            satelliteId: props.satellite?.satelliteId || 0,
            programId: props.programId,
            title: props.satellite?.title,
            city: props.satellite?.city,
            state: props.satellite?.state,
            postalCode: props.satellite?.postalCode,
            satalliteTypeId: SatelliteType['Satellite Campus'],
            address: props.satellite?.address,
            dateCreated: props.satellite?.dateCreated
                ? moment.utc(props.satellite.dateCreated).format('YYYY-MM-DD')
                : moment().format('YYYY-MM-DD'),
            isActive: props.satellite?.isActive ?? true,
        },
    });

    const onSubmit = useCallback(
        async (values: SatelliteFormData): Promise<void> => {
            // Skip if no changes for existing records
            if (props.satellite && !isDirty) {
                props.onCancel();
                return;
            }

            const toastId = toast.info('Saving Satellite Campus...');

            try {
                // Convert form data to API format
                const apiData: Satellite = {
                    satelliteId: values.satelliteId,
                    programId: values.programId,
                    title: values.title,
                    city: values.city,
                    state: values.state,
                    postalCode: values.postalCode,
                    satalliteTypeId: values.satalliteTypeId,
                    address: values.address,
                    dateCreated: values.dateCreated ? new Date(values.dateCreated) : new Date(),
                    isActive: values.isActive,
                };

                await programService.saveSatelliteCampuses([apiData]);

                toast.update(toastId, {
                    type: 'success',
                    render: 'Satellite Campus saved successfully',
                    autoClose: 3000,
                });

                props.onSave();
            } catch (error) {
                console.error('Save error:', error);
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Satellite Campus',
                    autoClose: 5000,
                });
            }
        },
        [isDirty, props],
    );

    const watchIsActive = watch('isActive');

    const handleToggleActive = useCallback(() => {
        setValue('isActive', false, { shouldDirty: true });
    }, [setValue]);

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="isActive"
                control={control}
                render={({ field }) => <input type="hidden" {...field} value={field.value ? 'true' : 'false'} />}
            />
            <Row>
                <Col>
                    <FormGroup>
                        <Label>Name</Label>
                        <Controller
                            name="title"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                />
                            )}
                        />
                        {errors.title && <span className="text-danger">{errors.title.message}</span>}
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label>Address</Label>
                        <Controller
                            name="address"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                />
                            )}
                        />
                        {errors.address && <span className="text-danger">{errors.address.message}</span>}
                    </FormGroup>
                </Col>
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label>City</Label>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                />
                            )}
                        />
                        {errors.city && <span className="text-danger">{errors.city.message}</span>}
                    </FormGroup>
                </Col>
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label>State</Label>
                        <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                >
                                    <option value="">{``}</option>
                                    {States &&
                                        States.map((s) => (
                                            <option key={s.abbreviation} value={s.abbreviation}>
                                                {s.abbreviation}
                                            </option>
                                        ))}
                                </Input>
                            )}
                        />
                        {errors.state && <span className="text-danger">{errors.state.message}</span>}
                    </FormGroup>
                </Col>
                <Col xs={12} md={3}>
                    <FormGroup>
                        <Label>Zip / Postal Code</Label>
                        <Controller
                            name="postalCode"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                />
                            )}
                        />
                        {errors.postalCode && <span className="text-danger">{errors.postalCode.message}</span>}
                    </FormGroup>
                </Col>
            </Row>

            <Row className={'mt-2'}>
                <Col xs={12} className={'d-flex flex-row-reverse'}>
                    <Button type="button" color={'link'} onClick={props.onCancel}>
                        Cancel
                    </Button>
                    <Button disabled={isSubmitting} type="submit" color={'primary'}>
                        Save
                    </Button>
                    {props.satellite && (
                        <Button
                            disabled={isSubmitting}
                            type="button"
                            color={watchIsActive ? 'danger' : 'secondary'}
                            className={'mr-5'}
                            onClick={handleToggleActive}
                        >
                            <i className={'mdi mdi-delete'} />
                            {watchIsActive && <span className={'ml-1'}>Remove Satellite Campus</span>}
                            {!watchIsActive && <span className={'ml-1'}>Click Save to Confirm</span>}
                        </Button>
                    )}
                </Col>
            </Row>
        </Form>
    );
};

export default SatelliteForm;
